import { Button, Checkbox, Radio, Select, Input } from "antd";
import { useState } from "react";
import { Download, Tag } from "react-feather";
import { useTranslation } from "react-i18next";
import { DOWNLOAD_PHOTOS, PATH_ALBUM } from "../../constants/album";
import { useGetAlbumApi, useCopyDrive, checkStatusCopy } from "../../services/apis/album.api";
import { IDownloadAlbum } from "../../types/album.type";
import { zipDownLoader } from "../../utils/common";
import { useCopyToClipboard } from "../../utils/clipboard";
import { CheckCircleFilled } from "@ant-design/icons";

const DownloadPhotos = (props: IDownloadAlbum) => {
  const { id, status, tags, totalPhotos, checkedPath } = props;
  const { t } = useTranslation();
  const [value, setValue] = useState<`${DOWNLOAD_PHOTOS}`>(DOWNLOAD_PHOTOS.ALL);
  const [currentTags, setCurrentTags] = useState<string[]>([]);
  const [loadingDown, setLoadingDown] = useState(false);
  const [downloadViaDrive, setDownloadViaDrive] = useState(true);
  
  // Chỉ hiển thị tính năng Drive khi ở dashboard
  const showDriveOption = checkedPath?.includes(PATH_ALBUM.DASHBOARD);
  const [folderName, setFolderName] = useState("");
  const [driveLink, setDriveLink] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { copyText, isCopied } = useCopyToClipboard();
  const { mutate: copyDriveToFolder, isLoading: loadingCopyDriveToFolder } = useCopyDrive();
  const options = { [value]: value !== DOWNLOAD_PHOTOS.TAG ? 1 : currentTags };
  const { isLoading, refetch } = useGetAlbumApi({
    perPage: totalPhotos,
    albumIdProps: id,
    filterParams: value !== DOWNLOAD_PHOTOS.ALL && options,
    enabled: false,
  });
  const disableBtn =
    isLoading ||
    loadingDown ||
    (value === "tag_ids" && currentTags.length === 0);
  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const onSubmit = async () => {
    if (downloadViaDrive && showDriveOption) {
      if (!folderName.trim()) {
        setError(t("dashboard.albums.action.download.photos.folderNameRequired"));
        return;
      }
      filterSaveGoogleDrive();
    } else {
      setLoadingDown(true);
      const { data: newData } = await refetch();
      await zipDownLoader({
        images: newData?.pages[0]?.data?.data?.files?.data,
        name: newData?.pages[0]?.data?.data.name,
      });
      setLoadingDown(false);
    }
  };

  const filterSaveGoogleDrive = () => {
    setDriveLink(null);
    setError(null);
    copyDriveToFolder(
      {
        id: id,
        data: {
          tag_ids: value === DOWNLOAD_PHOTOS.TAG ? currentTags : [],
          is_liked: value === DOWNLOAD_PHOTOS.LIKE,
          is_commented: value === DOWNLOAD_PHOTOS.COMMENT,
          is_recommended: value === DOWNLOAD_PHOTOS.RECOMMEND,
          folder_name: folderName,
        },
      },
      {
        onSuccess: (data) => {
          if (data.data.success) {
            setIsChecking(true);
            const interval = setInterval(() => {
              checkStatusCopy(data.data.folder_id)
                .then((res) => {
                  if (res.data.success === true) {
                    setDriveLink(
                      "https://drive.google.com/drive/folders/" +
                        data.data.folder_id
                    );
                    setIsChecking(false);
                    clearInterval(interval);
                  }
                })
                .catch((error) => {
                  console.error("Error checking status:", error);
                  setError(t("dashboard.albums.action.download.photos.statusCheckError"));
                  setIsChecking(false);
                  clearInterval(interval);
                });
            }, 2000);
          } else {
            setError(t("dashboard.albums.action.download.photos.createFolderError"));
          }
        },
        onError: (error) => {
          console.error("Error copying to Drive:", error);
          setError(error.response?.data?.message || t("dashboard.albums.action.download.photos.driveError"));
        },
      }
    );
  };

  return (
    <div className="flex flex-col">
      <div className="text-xs text-slate-500 mb-2">
        {t("dashboard.albums.action.download.photos.note")}
      </div>
      <span className="font-medium">
        {t("dashboard.albums.action.download.photos.title")}
      </span>
      
      {/* Drive Download Option - Chỉ hiển thị khi ở dashboard */}
      {showDriveOption && (
        <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <Checkbox
            checked={downloadViaDrive}
            onChange={(e) => setDownloadViaDrive(e.target.checked)}
            className="font-medium"
          >
            {t("dashboard.albums.action.download.photos.downloadViaDrive")}
          </Checkbox>
          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1 ml-6">
            {t("dashboard.albums.action.download.photos.driveNote")}
          </div>
          {downloadViaDrive && (
            <div className="mt-3 ml-6">
              <Input
                placeholder={t("dashboard.albums.action.download.photos.folderNamePlaceholder")}
                value={folderName}
                onChange={(e) => setFolderName(e.target.value)}
                className="w-full"
              />
            </div>
          )}
        </div>
      )}

      <Radio.Group
        name="download-photos"
        className="flex flex-col gap-1"
        onChange={handleChange}
        value={value}
      >
        {Object.values(DOWNLOAD_PHOTOS).map((item) => {
          const disable = !status[item] && item !== DOWNLOAD_PHOTOS.ALL;
          return (
            <Radio
              value={item}
              key={item}
              disabled={disable}
              className={`${disable ? "ml-4" : ""}`}
            >
              <span className="text-nowrap">
                {t(`dashboard.albums.action.download.photos.${item}`)}
              </span>
            </Radio>
          );
        })}
      </Radio.Group>
      {status.tag_ids && (
        <div className="sm:w-11/12 ml-6 w-10/12 flex flex-col gap-1 mt-1">
          <Select
            mode="multiple"
            value={currentTags}
            onChange={setCurrentTags}
            suffixIcon={<Tag className="w-4" />}
            placeholder={t("dashboard.albums.action.download.photos.chooseTag")}
            options={tags?.map((item) => ({
              value: item.id,
              label: item.name,
            }))}
          />
          {value === DOWNLOAD_PHOTOS.TAG && currentTags.length === 0 && (
            <span className="text-xs block text-red-500">
              {t("dashboard.albums.action.download.photos.minTag")}
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2 my-4">
        <Button
          type="default"
          htmlType="submit"
          className={`transition-all success ${
            disableBtn || (downloadViaDrive && showDriveOption && !folderName.trim()) ? "opacity-70" : ""
          }`}
          disabled={disableBtn || (downloadViaDrive && showDriveOption && !folderName.trim())}
          loading={loadingDown || loadingCopyDriveToFolder}
          onClick={() => onSubmit()}
        >
          {!loadingDown && !loadingCopyDriveToFolder && <Download className="w-4" />}
          {downloadViaDrive && showDriveOption
            ? t("dashboard.albums.action.download.photos.downloadViaDrive")
            : t("dashboard.albums.action.download.photos.btn")
          }
        </Button>
        
        {/* Drive Link Actions */}
        {downloadViaDrive && driveLink && (
          <div className="flex gap-2">
            <Button
              type="primary"
              className="flex-1"
              onClick={() => window.open(driveLink, "_blank")}
            >
              {t("dashboard.albums.action.download.photos.viewDriveFolder")}
            </Button>
            <Button
              type="default"
              className="flex-1"
              onClick={() => copyText(driveLink)}
            >
              {t("dashboard.albums.action.download.photos.copyLink")}
              {isCopied && <CheckCircleFilled className="text-green-500 ml-1" />}
            </Button>
          </div>
        )}
      </div>
      {loadingDown && (
        <span className="text-center text-orange-600 whitespace-pre-line">
          {t("dashboard.albums.action.download.photos.loading")}
        </span>
      )}
      
      {isChecking && (
        <span className="text-center text-blue-600 whitespace-pre-line">
          {t("dashboard.albums.action.download.photos.driveUploading")}
        </span>
      )}
      
      {driveLink && (
        <span className="text-center text-green-600">
          {t("dashboard.albums.action.download.photos.driveSuccess")}
        </span>
      )}
      
      {error && (
        <span className="text-center text-red-600">
          {error}
        </span>
      )}
    </div>
  );
};

export default DownloadPhotos;
